// =========================================================================
// The Ledger on a USB cable, spoken to directly — no wallet app in between.
//
// Chrome on the Mac reaches the device through WebHID. The page asks it three
// things, and nothing else:
//
//   which app is open                 B0 01 — answered by the device itself
//   the address of the key at a path  E0 02 — nothing is shown on the device;
//                                             with P2 = 1 the account's chain
//                                             code comes too, and with P1 = 1
//                                             the device shows the address and
//                                             waits for a press (registering a
//                                             key, exportAccountKey below)
//   sign these bytes with that key    E0 04 — the device shows the transfer
//                                             and waits for a press
//
// Written from Ledger's own published sources — the TRON app's docs/APDU.md,
// src/handlers and src/app_errors.h; the HID framing of @ledgerhq/devices;
// hw-app-trx — rather than taken as Ledger's libraries, which are a tree of
// packages, on the one page where a compromise moves money, for a couple of
// hundred lines of byte layout.
//
// THIS MODULE DOES NOT DECIDE WHAT IS SIGNED. It is handed the bytes the page
// decoded, showed and checked, and sends exactly those. The device hashes them
// itself and signs the hash; the server recovers the signer from the same
// bytes and refuses any key but the one it named.
//
// It needs no curve maths either: the device says which address a path holds,
// and the server checks the signature. So the page still carries only the two
// libraries it had (dev/check-bundle.mjs holds it to that).
//
// PROVEN AGAINST LEDGER'S REAL TRON APP in Ledger's own emulator (16 October:
// dev/emulator-check.mjs, 16 of 16 — the commands, the answers, the screens).
// The unit tests run against test/fake-ledger.ts. Neither reaches the last inch
// — Chrome's WebHID and the USB cable: the first tap on a real Ledger does.
// =========================================================================

import { sha256 } from '@noble/hashes/sha256';
import { ripemd160 } from '@noble/hashes/ripemd160';
import { decodeTronAddress } from '../../signer/src/address.mjs';
import { printable } from '../../signer/src/printable.mjs';
import type { Network } from './verify.ts';

/** Every Ledger answers to this USB vendor id. */
export const LEDGER_VENDOR_ID = 0x2c97;

/** One HID report, each way. */
const PACKET = 64;
const TAG_APDU = 0x05;

const CLA_OS = 0xb0;
const INS_APP_AND_VERSION = 0x01;
const CLA_TRON = 0xe0;
const INS_ADDRESS = 0x02;
const INS_SIGN = 0x04;
/** Sign a personal message (TIP-191): the app hashes "\x19TRON Signed Message:\n" + the byte length + the text and shows the text. */
const INS_SIGN_MESSAGE = 0x08;

// GET_PUBLIC_KEY's two parameters (src/handlers/handlers.h): P1 = 1 shows the
// address on the device and waits for a press; P2 = 1 adds the account's
// chain code to the answer. Both 0 is the plain question signing asks.
const P1_ADDRESS_SHOW = 0x01;
const P2_CHAIN_CODE = 0x01;

// How the TRON app is told where a transaction's bytes stand (src/handlers/handlers.h).
const P1_SIGN_WHOLE = 0x10;
const P1_SIGN_FIRST = 0x00;
const P1_SIGN_MORE = 0x80;
const P1_SIGN_LAST = 0x90;

/** Bytes of one SIGN command's data, path included — hw-app-trx's CHUNK_SIZE. */
export const SIGN_CHUNK = 250;

/** How many of the device's TRON accounts are asked for the key the desk named. */
export const ACCOUNTS_SEARCHED = 10;

const OK = 0x9000;
const HARDENED = 0x8000_0000;

/** The longest answer this page reads: an address with its chain code is 135 bytes. */
const MAX_ANSWER = 512;

/** A question the device should answer at once. */
const ANSWER_WAIT_MS = 15_000;
/** The signing question waits for a person to read the screens and press. */
const SIGN_WAIT_MS = 5 * 60_000;

export class LedgerError extends Error {
  // Written out rather than declared as parameter properties: those emit code,
  // and Node's strip-only TypeScript support (which runs the tests) rejects them.
  readonly remedy?: string;
  readonly status?: number;

  constructor(message: string, remedy?: string, status?: number) {
    super(message);
    this.name = 'LedgerError';
    this.remedy = remedy;
    this.status = status;
  }
}

// ---- WebHID, as far as this page uses it --------------------------------

export interface HidReport { data: DataView }

export interface HidDeviceLike {
  readonly vendorId: number;
  readonly opened: boolean;
  open(): Promise<void>;
  close(): Promise<void>;
  sendReport(reportId: number, data: Uint8Array): Promise<void>;
  addEventListener(type: 'inputreport', listener: (event: HidReport) => void): void;
  removeEventListener(type: 'inputreport', listener: (event: HidReport) => void): void;
}

export interface HidConnectionEvent { device: HidDeviceLike }

export interface HidLike {
  getDevices(): Promise<HidDeviceLike[]>;
  requestDevice(options: { filters: Array<{ vendorId: number }> }): Promise<HidDeviceLike[]>;
  /** 'disconnect' fires when a device is unplugged — and when its app is opened or closed. */
  addEventListener?(type: 'disconnect', listener: (event: HidConnectionEvent) => void): void;
  removeEventListener?(type: 'disconnect', listener: (event: HidConnectionEvent) => void): void;
}

/** WebHID where the browser has it: Chrome and Edge on a computer. No iPhone browser has it. */
export function webHid(): HidLike | null {
  const hid = (globalThis as { navigator?: { hid?: unknown } }).navigator?.hid as Partial<HidLike> | undefined;
  return hid && typeof hid.requestDevice === 'function' && typeof hid.getDevices === 'function'
    ? (hid as HidLike)
    : null;
}

// ---- bytes ----------------------------------------------------------------

const hexOf = (b: Uint8Array): string => [...b].map((x) => x.toString(16).padStart(2, '0')).join('');

function bytesOf(hex: string): Uint8Array {
  if (!/^([0-9a-fA-F]{2})*$/.test(hex)) throw new LedgerError('The transaction bytes are not hex', 'Nothing was signed. Report this.');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

const ascii = (b: Uint8Array): string => String.fromCharCode(...b);

/** An APDU: class, instruction, two parameters, and up to 255 bytes of data. */
export function apdu(cla: number, ins: number, p1: number, p2: number, data: Uint8Array = new Uint8Array(0)): Uint8Array {
  if (data.length > 255) throw new Error('an APDU carries at most 255 bytes of data');
  const out = new Uint8Array(5 + data.length);
  out.set([cla, ins, p1, p2, data.length]);
  out.set(data, 5);
  return out;
}

/**
 * One APDU as the 64-byte reports that carry it: each starts with the channel,
 * the tag and its sequence number; the first then says how long the APDU is.
 * The last report is padded with zeros.
 */
export function frame(channel: number, command: Uint8Array): Uint8Array[] {
  const body = new Uint8Array(2 + command.length);
  body[0] = command.length >> 8;
  body[1] = command.length & 0xff;
  body.set(command, 2);
  const room = PACKET - 5;
  const packets: Uint8Array[] = [];
  for (let seq = 0, at = 0; at < body.length; seq++, at += room) {
    const p = new Uint8Array(PACKET);
    p.set([channel >> 8, channel & 0xff, TAG_APDU, seq >> 8, seq & 0xff]);
    p.set(body.subarray(at, at + room), 5);
    packets.push(p);
  }
  return packets;
}

/** The device's answer, put back together from its reports. */
export class Reassembly {
  private readonly channel: number;
  private seq = 0;
  private expected = 0;
  private got: number[] = [];

  constructor(channel: number) {
    this.channel = channel;
  }

  /** Add one report: the whole answer once it is complete, null until then. */
  add(report: Uint8Array): Uint8Array | null {
    const head = this.seq === 0 ? 7 : 5;
    if (report.length < head) throw new LedgerError('The Ledger sent a reply too short to read', 'Unplug it, plug it back in, and try again.');
    if (((report[0]! << 8) | report[1]!) !== this.channel || report[2] !== TAG_APDU || ((report[3]! << 8) | report[4]!) !== this.seq) {
      throw new LedgerError('The Ledger sent a reply out of order', 'Close every other app or tab using the Ledger, unplug it, plug it back in, and try again.');
    }
    if (this.seq === 0) {
      this.expected = (report[5]! << 8) | report[6]!;
      if (this.expected > MAX_ANSWER) {
        throw new LedgerError('The Ledger announced a reply far longer than any answer', 'Unplug it, plug it back in, and try again.');
      }
    }
    this.seq++;
    for (const b of report.subarray(head)) {
      if (this.got.length === this.expected) break;
      this.got.push(b);
    }
    return this.got.length === this.expected ? Uint8Array.from(this.got) : null;
  }
}

// ---- paths -----------------------------------------------------------------

/** The key a Ledger account holds, as Ledger Live and TronLink number them: m/44'/195'/account'/0/0. */
export function accountPath(account: number): number[] {
  return [44 + HARDENED, 195 + HARDENED, account + HARDENED, 0, 0];
}

/** The account's own node, m/44'/195'/account' — the key every deposit address of the account hangs from. */
export function accountKeyPath(account: number): number[] {
  return [44 + HARDENED, 195 + HARDENED, account + HARDENED];
}

/** The coin's node, m/44'/195' — the parent an account key names by its fingerprint. The app answers a two-step path. */
const COIN_PATH = [44 + HARDENED, 195 + HARDENED];

/** The TRON app takes at most ten steps (src/parse.h MAX_BIP32_PATH). */
const MAX_PATH_STEPS = 10;

/**
 * The register's derivation as a device path, or null when the text is not
 * one this page would send — nothing is guessed from it.
 *
 *   "0/7"      change 0, index 7 under the account whose key is registered
 *              (the account given, 0 unless the caller knows better)
 *   "1'/0/0"   starts with a hardened step: an account of its own under the
 *              coin — the stage-1 rows, an operator's account as a customer's
 *              address
 */
export function derivationPath(derivation: string, account = 0): number[] | null {
  if (typeof derivation !== 'string' || !Number.isInteger(account) || account < 0 || account >= HARDENED) return null;
  const steps: number[] = [];
  for (const part of derivation.trim().split('/')) {
    const m = /^(\d{1,10})(['h])?$/.exec(part);
    if (!m) return null;
    const n = Number(m[1]);
    if (n >= HARDENED) return null;
    steps.push(m[2] ? n + HARDENED : n);
  }
  if (steps.length === 0) return null;
  const path = steps[0]! >= HARDENED ? [...COIN_PATH, ...steps] : [...accountKeyPath(account), ...steps];
  return path.length > MAX_PATH_STEPS ? null : path;
}

/**
 * A desk DESTINATION's path, for the proof on the device: change/index under the account the entry names, and nothing
 * else. derivationPath reads a hardened first step as an account of its own under the coin, so "0'/0/7" with account 3
 * proved m/44'/195'/0'/0/7 — an address outside the account the card names — as Treasury (the pre-live red team and
 * Astra, 29 Oct 2026). Null for anything but two plain steps.
 */
export function destinationPath(derivation: string, account: number): number[] | null {
  // Two plain steps; derivationPath bounds each one.
  return typeof derivation === 'string' && /^\d+\/\d+$/.test(derivation.trim()) ? derivationPath(derivation, account) : null;
}

export function pathText(path: number[]): string {
  return `m/${path.map((n) => (n >= HARDENED ? `${n - HARDENED}'` : String(n))).join('/')}`;
}

/** A path as the device reads it: how many steps, then each as four bytes. */
export function encodePath(path: number[]): Uint8Array {
  const out = new Uint8Array(1 + path.length * 4);
  const view = new DataView(out.buffer);
  out[0] = path.length;
  path.forEach((n, i) => view.setUint32(1 + i * 4, n, false));
  return out;
}

// ---- the transaction, cut where the device can take it ----------------------

/** Where the varint starting at `at` ends. */
function varintEnd(b: Uint8Array, at: number): number {
  for (let i = at; i < b.length && i < at + 10; i++) if ((b[i]! & 0x80) === 0) return i + 1;
  throw new LedgerError('The transaction bytes end in the middle of a field', 'Nothing was signed. Report this.');
}

function varintValue(b: Uint8Array, at: number): { value: number; end: number } {
  let value = 0;
  let scale = 1;
  const end = varintEnd(b, at);
  for (let i = at; i < end; i++) {
    value += (b[i]! & 0x7f) * scale;
    scale *= 128;
  }
  return { value, end };
}

/** Length of the whole top-level field at `at`: its tag, and its value. */
function fieldLength(raw: Uint8Array, at: number): number {
  const tag = varintValue(raw, at);
  const wire = tag.value % 8;
  if (wire === 0) return varintEnd(raw, tag.end) - at;
  if (wire === 2) {
    const len = varintValue(raw, tag.end);
    return len.end + len.value - at;
  }
  throw new LedgerError(`The transaction carries a field of wire type ${wire}`, 'A TRON transaction has none. Nothing was signed. Report this.');
}

/**
 * The SIGN commands for one transaction: the path, then the bytes, never more
 * than SIGN_CHUNK to a command, and cut only between whole top-level fields —
 * the TRON app reads the transaction as it arrives and hw-app-trx cuts the same
 * way. A desk transfer fits in one.
 */
export function signChunks(path: number[], raw: Uint8Array): Uint8Array[] {
  const chunks: Uint8Array[] = [];
  let current: number[] = [...encodePath(path)];
  const pathBytes = current.length;
  let at = 0;
  while (at < raw.length) {
    const len = fieldLength(raw, at);
    if (at + len > raw.length) throw new LedgerError('A transaction field runs past the end of its bytes', 'Nothing was signed. Report this.');
    if (len > SIGN_CHUNK) throw new LedgerError('A transaction field is too large for the device', 'Nothing was signed. Report this.');
    if (current.length + len > SIGN_CHUNK) {
      // A first command carrying the path alone reads to the app as a finished,
      // empty transaction, which it refuses (parse.c). A TRON transaction opens
      // with 4 bytes of ref_block_bytes, so this is never reached by one.
      if (chunks.length === 0 && current.length === pathBytes) {
        throw new LedgerError('The transaction opens with a field too large for the device', 'Nothing was signed. Report this.');
      }
      chunks.push(Uint8Array.from(current));
      current = [];
      continue;
    }
    current.push(...raw.subarray(at, at + len));
    at += len;
  }
  chunks.push(Uint8Array.from(current));
  return chunks;
}

/** P1 for each SIGN command: the whole transaction in one, or first / more / last. */
export function signFlags(count: number): number[] {
  if (count === 1) return [P1_SIGN_WHOLE];
  return Array.from({ length: count }, (_, i) => (i === 0 ? P1_SIGN_FIRST : i === count - 1 ? P1_SIGN_LAST : P1_SIGN_MORE));
}

// ---- what the device says ----------------------------------------------------

/** "Which app is open": format 1, then the app's name and version, each with its length. */
export function parseAppAndVersion(data: Uint8Array): { name: string; version: string } {
  const unreadable = () => new LedgerError('The Ledger did not say which app is open', 'Unplug it, plug it back in, open the TRON app, and try again.');
  if (data[0] !== 1) throw unreadable();
  let at = 1;
  const read = () => {
    const len = data[at];
    if (len === undefined || at + 1 + len > data.length) throw unreadable();
    const text = ascii(data.subarray(at + 1, at + 1 + len));
    at += 1 + len;
    return text;
  };
  const name = read();
  return { name, version: read() };
}

/** The address of the key at a path: a 65-byte public key, then the address as 34 characters. */
export function parseAddressAnswer(data: Uint8Array): { publicKey: Uint8Array; address: string } {
  const unreadable = () => new LedgerError('The Ledger answered with an address this page cannot read', 'Report this.');
  if (data[0] !== 65 || data[66] !== 34 || data.length < 1 + 65 + 1 + 34) throw unreadable();
  const address = ascii(data.subarray(67, 67 + 34));
  try {
    decodeTronAddress(address);
  } catch {
    throw unreadable();
  }
  return { publicKey: data.slice(1, 66), address };
}

/**
 * The account's key with its chain code (P2 = 1): the address answer, then
 * the 32 bytes of chain code — with NO length byte before them. That is how
 * the app writes it (src/helpers.c helper_send_response_pubkey: the key and
 * the address each behind their length, the chain code copied bare), so the
 * answer is exactly 133 bytes, and an answer of any other length is not one.
 */
export function parseAccountKeyAnswer(data: Uint8Array): { publicKey: Uint8Array; address: string; chainCode: Uint8Array } {
  const unreadable = () => new LedgerError('The Ledger answered with a key this page cannot read', 'Report this.');
  if (data.length !== 1 + 65 + 1 + 34 + 32) throw unreadable();
  const { publicKey, address } = parseAddressAnswer(data);
  // The app answers the uncompressed point: 04, then X and Y.
  if (publicKey[0] !== 0x04) throw unreadable();
  return { publicKey, address, chainCode: data.slice(101, 133) };
}

/**
 * A key's fingerprint as BIP32 names a parent: the first four bytes of
 * hash160 of the COMPRESSED key. Compressing an uncompressed point needs no
 * curve maths — the prefix is 02 or 03 by the parity of Y, then X.
 */
export function keyFingerprint(uncompressed: Uint8Array): string {
  if (uncompressed.length !== 65 || uncompressed[0] !== 0x04) throw new LedgerError('The Ledger answered with a key this page cannot read', 'Report this.');
  const compressed = new Uint8Array(33);
  compressed[0] = (uncompressed[64]! & 1) ? 0x03 : 0x02;
  compressed.set(uncompressed.subarray(1, 33), 1);
  return hexOf(ripemd160(sha256(compressed)).subarray(0, 4));
}

const statusText = (status: number) => `0x${status.toString(16).padStart(4, '0')}`;

/**
 * A refusal, in the words the person holding the device needs. The codes are
 * the TRON app's own (src/app_errors.h) and the device's.
 */
export function refusal(status: number, signing: boolean, network: Network): LedgerError {
  switch (status) {
    case 0x6985:
      return signing
        ? new LedgerError('Rejected on the device', 'Nothing was signed. You can close this page.', status)
        : new LedgerError('The Ledger declined', 'Unlock it, open the TRON app, and try again.', status);
    case 0x5515:
      return new LedgerError('Your Ledger is locked', 'Unlock it with your PIN, open the TRON app, and tap Sign again.', status);
    case 0x6901:
      // The device takes no new command while an earlier one still waits for
      // its answer — a transfer left on its screen by a reload, another tab,
      // or this page giving up on it.
      return new LedgerError(
        'Your Ledger is still showing an earlier request',
        'Reject it on the Ledger’s screen, then tap Sign again.',
        status,
      );
    case 0x6982:
      // In the TRON app: the key could not be derived, or signing failed after
      // the transfer was approved. Not a locked device (that is 0x5515).
      return new LedgerError(
        'The Ledger could not use the key',
        'Nothing was signed. Unlock it, open the TRON app and tap Sign again; if it happens again, report the code 0x6982.',
        status,
      );
    case 0x6e00:
    case 0x6d00:
    case 0x6e01:
    case 0x6511:
      return new LedgerError('The TRON app is not open on your Ledger', 'Open the TRON app on the device, then tap Sign again.', status);
    case 0x6a8c:
      // TRON app 0.7.x: every personal message is refused until the app's own
      // "Sign by hash" setting is allowed (handleSignPersonalMessage's first
      // line; the operator's Nano, 22 Oct 2026). The device then shows the
      // message's hash, never its text.
      return new LedgerError(
        'The TRON app needs "Sign by hash" allowed',
        'On the Ledger: TRON app → Settings → Sign by hash → Allowed. Then tap again; the device shows the message\u2019s hash to approve.',
        status,
      );
    case 0x6a8d:
      // The app shows an amount and a recipient only for tokens on its own
      // list. Test-network USDT is not on it, so the app treats the transfer
      // as a call to an unknown contract — which it signs only with this
      // setting on. Real USDT is on the list: on mainnet this code means
      // something is wrong, and the setting must stay off.
      return network === 'nile'
        ? new LedgerError(
          'Your Ledger does not know test-network USDT',
          'For this test only: on the Ledger open the TRON app, then Settings, and turn on "Custom contracts". '
            + 'Tap Sign again. Turn the setting off when the test is over.',
          status,
        )
        : new LedgerError(
          'Your Ledger did not recognise this as a USDT transfer',
          'Do not turn on "Custom contracts". Nothing was signed. Report this.',
          status,
        );
    case 0x6a8b:
      return new LedgerError(
        'The TRON app refused: this transaction carries extra data',
        'This page never sends any. Do not change the setting — nothing was signed. Report this.',
        status,
      );
    default:
      return new LedgerError(`The Ledger answered with code ${statusText(status)}`, 'Nothing was signed. Report this code.', status);
  }
}

// ---- one conversation with one device ----------------------------------------

const gone = () => new LedgerError(
  'The Ledger was disconnected',
  'If the cable came out or its app was closed, plug it back in, unlock it, open the TRON app, and tap Sign again.',
);

/** The cable to one device: commands out, answers back, one at a time. */
export class LedgerLink {
  private readonly device: HidDeviceLike;
  private readonly channel: number;
  private queued: Uint8Array[] = [];
  private waiting: ((report: Uint8Array) => void) | null = null;
  /**
   * Set when a question went unanswered. The device may still answer it later,
   * and that answer would look exactly like the answer to the next question —
   * same channel, sequence 0. So nothing more is asked on this link.
   */
  private gaveUp = false;
  /** Set when the browser says the device went away: unplugged, or its app closed. */
  private disconnected = false;
  private readonly onReport = (event: HidReport) => {
    const v = event.data;
    const report = new Uint8Array(v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength));
    if (this.waiting) this.waiting(report);
    else this.queued.push(report);
  };

  constructor(device: HidDeviceLike, channel = Math.floor(Math.random() * 0xffff)) {
    this.device = device;
    this.channel = channel;
    device.addEventListener('inputreport', this.onReport);
  }

  private nextReport(waitMs: number): Promise<Uint8Array> {
    const queued = this.queued.shift();
    if (queued) return Promise.resolve(queued);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.waiting = null;
        this.gaveUp = true;
        reject(new LedgerError(
          'The Ledger did not answer in time',
          'If it is still showing something, reject it there. Then check the cable and that the TRON app is open, and tap Sign again.',
        ));
      }, waitMs);
      this.waiting = (report) => {
        clearTimeout(timer);
        this.waiting = null;
        resolve(report);
      };
    });
  }

  /**
   * Send one APDU; the answer's data and its two-byte status. `waitMs` is for
   * the whole answer, not each report of it: a device that sends one report
   * just inside the limit, again and again, still runs out of time.
   * `onSent` runs once the whole command has gone out.
   */
  async exchange(command: Uint8Array, waitMs: number, onSent?: () => void): Promise<{ status: number; data: Uint8Array }> {
    if (this.disconnected) throw gone();
    if (this.gaveUp) {
      throw new LedgerError('The Ledger did not answer an earlier question', 'Unplug it, plug it back in, and tap Sign again.');
    }
    try {
      for (const packet of frame(this.channel, command)) await this.device.sendReport(0, packet);
    } catch {
      throw new LedgerError('The Ledger could not be reached', 'Check the cable, unlock the device, open the TRON app, and tap Sign again.');
    }
    onSent?.();
    const deadline = Date.now() + waitMs;
    const answer = new Reassembly(this.channel);
    for (;;) {
      const report = await this.nextReport(Math.max(0, deadline - Date.now()));
      if (this.disconnected) throw gone();
      const whole = answer.add(report);
      if (whole === null) continue;
      if (whole.length < 2) throw new LedgerError('The Ledger answered with nothing', 'Unplug it, plug it back in, and try again.');
      return { status: (whole[whole.length - 2]! << 8) | whole[whole.length - 1]!, data: whole.subarray(0, whole.length - 2) };
    }
  }

  /** The device went away: whatever waits for it is told now, not when its time runs out. */
  lost(): void {
    this.disconnected = true;
    this.waiting?.(new Uint8Array(0));
  }

  async close(): Promise<void> {
    this.device.removeEventListener('inputreport', this.onReport);
    try {
      await this.device.close();
    } catch {
      /* already gone: unplugged, or closed by the browser */
    }
  }
}

/** The Ledger this origin was allowed before, or the one the person picks now. */
async function connect(hid: HidLike): Promise<HidDeviceLike> {
  let device: HidDeviceLike | undefined;
  try {
    device = (await hid.getDevices()).find((d) => d.vendorId === LEDGER_VENDOR_ID);
    if (!device) [device] = await hid.requestDevice({ filters: [{ vendorId: LEDGER_VENDOR_ID }] });
  } catch (err) {
    // Closing the browser's list is not an error (it answers with no device).
    // A refusal is: the site's Permissions-Policy forbids WebHID, or the tap
    // that asked was too long ago.
    if ((err as { name?: unknown } | null)?.name === 'SecurityError') {
      throw new LedgerError(
        'The browser would not let this page reach a Ledger',
        'Tap Sign again. If this happens again, report it — the site may be set up to block it.',
      );
    }
    device = undefined;
  }
  if (!device) {
    throw new LedgerError(
      'No Ledger was chosen',
      'Plug the Ledger into this computer with its cable, unlock it, open the TRON app, tap Sign again, and pick it in the list the browser shows.',
    );
  }
  if (!device.opened) {
    try {
      await device.open();
    } catch {
      throw new LedgerError(
        'The Ledger is busy',
        'Close Ledger Live, TronLink and any other app or tab using the Ledger, then tap Sign again.',
      );
    }
  }
  return device;
}

async function ask(
  link: LedgerLink, command: Uint8Array, network: Network, signing = false, waitMs = ANSWER_WAIT_MS, onSent?: () => void,
): Promise<Uint8Array> {
  const { status, data } = await link.exchange(command, waitMs, onSent);
  if (status !== OK) throw refusal(status, signing, network);
  return data;
}

export type LedgerStep = 'connect' | 'app' | 'key' | 'show' | 'sign';

export interface LedgerSigning {
  hid: HidLike;
  /** The address whose key must sign: the card names it, and the server takes no other. */
  signer: string;
  /** The bytes the page decoded, showed and checked. The device is sent exactly these. */
  rawDataHex: string;
  network: Network;
  /**
   * The register's derivation of the signing address, when the card carries
   * one: "0/7" under the registered account, or "1'/0/0" for an account of
   * its own. Tried first; the account search follows if it does not hold the
   * key. A hint only — the key the device signs with is checked against the
   * signer here, and the server checks the signature against the same.
   */
  derivation?: string | null;
  /** The account the registered key is: what "0/7" is under. 0 unless the card says otherwise. */
  account?: number;
  /**
   * One of the desk's own wallets as the destination: the Ledger account and
   * the register's path it was issued from, the address the bytes pay, and
   * its name. The device derives the address there FIRST, with nothing shown
   * on it, and the transfer is not sent to it unless the two are equal
   * (audit of 21 Oct 2026, finding 1: the server's label was enough before).
   */
  prove?: { account: number; derivation: string; expect: string; name: string } | null;
  /** Each step, for the status line. 'sign' is the moment the whole transfer has reached the device. */
  onStep: (step: LedgerStep, text: string) => void;
}

/** The connected device, its app checked to be TRON. The caller lets go of it in its finally. */
async function openTronApp(hid: HidLike, network: Network, onStep: (step: LedgerStep, text: string) => void, again: string): Promise<{ link: LedgerLink; release: () => void }> {
  onStep('connect', 'Connecting to your Ledger. If the browser shows a list, choose the Ledger in it.');
  const device = await connect(hid);
  const link = new LedgerLink(device);
  const onDisconnect = (event: HidConnectionEvent) => { if (event.device === device) link.lost(); };
  hid.addEventListener?.('disconnect', onDisconnect);
  const release = () => { hid.removeEventListener?.('disconnect', onDisconnect); };
  try {
    onStep('app', 'Checking that the TRON app is open…');
    const { name } = parseAppAndVersion(await ask(link, apdu(CLA_OS, INS_APP_AND_VERSION, 0, 0), network));
    if (name === 'BOLOS') {
      throw new LedgerError('No app is open on your Ledger', `Open the TRON app on the device, then tap ${again} again.`);
    }
    if (name !== 'Tron') {
      throw new LedgerError(`The ${printable(name)} app is open on your Ledger`, `Close it, open the TRON app, then tap ${again} again.`);
    }
  } catch (err) {
    release();
    await link.close();
    throw err;
  }
  return { link, release };
}

/**
 * Sign over the cable: find the key the desk named — at the path the register
 * recorded for it, else among the device's TRON accounts — and have the
 * device sign the bytes with it.
 */
export async function signOnLedger(o: LedgerSigning): Promise<{ signature: string; path: string }> {
  const raw = bytesOf(o.rawDataHex);
  const hinted = o.derivation ? derivationPath(o.derivation, o.account ?? 0) : null;
  // Bytes the device could not take are refused before it is touched. Where
  // they are cut depends only on lengths: the longest path that may be sent.
  signChunks(hinted && hinted.length > accountPath(0).length ? hinted : accountPath(0), raw);

  const { link, release } = await openTronApp(o.hid, o.network, o.onStep, 'Sign');
  try {
    // Nothing is shown on the device for these: they only ask which address
    // a path's key has. The first match is the key the desk named.
    let path: number[] | null = null;
    const addressAt = async (candidate: number[]) =>
      parseAddressAnswer(await ask(link, apdu(CLA_TRON, INS_ADDRESS, 0, 0, encodePath(candidate)), o.network)).address;

    // WHERE THE MONEY GOES, proven on the device before any key is looked
    // for: the desk's own wallet must be the address this Ledger derives at
    // the account and path the register issued it from. Nothing else on this
    // page can vouch for a desk destination — the server labels it, and the
    // server is what a forged card comes from.
    if (o.prove) {
      const at = destinationPath(o.prove.derivation, o.prove.account);
      if (!at) {
        throw new LedgerError(
          'Cannot check the destination',
          // The path is the server's free text: not echoed (round 7).
          `The desk recorded a path for ${o.prove.name} this page cannot send. Nothing was signed — report this.`,
        );
      }
      o.onStep('key', `Checking ${o.prove.name} on your Ledger (account ${o.prove.account + 1}, ${pathText(at)})…`);
      const derived = await addressAt(at);
      if (derived !== o.prove.expect) {
        throw new LedgerError(
          'Mismatch — do not sign',
          // The Ledger's own derived tail only — never the destination's (round 7: its last eight held the ending).
          `Your Ledger derives …${derived.slice(-8)} at account ${o.prove.account + 1}, ${pathText(at)} — not the address this card pays. This card does not pay one of your wallets. Nothing was signed — report it.`,
        );
      }
      o.onStep('key', `${o.prove.name} checked: this Ledger derives the address the card pays.`);
    }

    // The path the register recorded for the address, first: for a deposit
    // address issued from the account key that is its only path, and one
    // question instead of up to ten.
    if (hinted) {
      o.onStep('key', `Asking your Ledger for the key at ${pathText(hinted)} — the path the desk recorded for this address…`);
      if (await addressAt(hinted) === o.signer) path = hinted;
    } else if (o.derivation) {
      o.onStep('key', 'The path the desk recorded for this address could not be read; looking through the accounts instead…');
    }
    for (let account = 0; account < ACCOUNTS_SEARCHED && path === null; account++) {
      const candidate = accountPath(account);
      if (hinted && candidate.every((n, i) => n === hinted[i]) && candidate.length === hinted.length) continue;   // asked already
      o.onStep('key', `Looking for the key the desk expects on your Ledger (account ${account + 1} of ${ACCOUNTS_SEARCHED})…`);
      if (await addressAt(candidate) === o.signer) path = candidate;
    }
    if (path === null) {
      throw new LedgerError(
        'This Ledger does not hold the key the desk expects',
        `The desk takes a signature only from ${o.signer}. It is not among the first ${ACCOUNTS_SEARCHED} TRON accounts on this Ledger${hinted ? `, nor at ${pathText(hinted)}` : ''} — check that it is the Ledger registered to you. Nothing was signed.`,
      );
    }

    // Which key, said here: the server names it, the device holds it, and
    // this line is where the operator can see which one it is.
    o.onStep('key', `Found the key the desk expects: account ${path[2]! - HARDENED + 1} on this Ledger (${pathText(path)}), ${o.signer.slice(0, 6)}…${o.signer.slice(-4)}. Sending the transfer to it…`);
    const chunks = signChunks(path, raw);
    const flags = signFlags(chunks.length);
    let answer: Uint8Array = new Uint8Array(0);
    for (let i = 0; i < chunks.length; i++) {
      const last = i === chunks.length - 1;
      // The device shows the transfer once the last piece has reached it —
      // only then has it been asked to sign anything.
      answer = await ask(
        link, apdu(CLA_TRON, INS_SIGN, flags[i]!, 0, chunks[i]!), o.network, true,
        last ? SIGN_WAIT_MS : ANSWER_WAIT_MS,
        last ? () => o.onStep('sign', 'Now check the transfer on your Ledger’s screen against this page, and approve it there.') : undefined,
      );
    }
    if (answer.length < 65) {
      throw new LedgerError('The Ledger did not return a signature', 'Nothing was sent to the desk. Report this.');
    }
    // r ‖ s ‖ v, as the TRON app writes it. Checked by the server, which
    // recovers the signer from the bytes it holds and refuses any other key.
    return { signature: hexOf(answer.subarray(0, 65)), path: pathText(path) };
  } finally {
    release();
    await link.close();
  }
}

// ---- a personal message, signed by a key of the device ------------------------

/** The TRON app's chunk for a personal message (Ledger's hw-app-trx signPersonalMessage): 150 bytes, not a transaction's. */
export const MESSAGE_CHUNK = 150;

/**
 * The message as the TRON app's SIGN PERSONAL MESSAGE takes it (Ledger's
 * hw-app-trx `signPersonalMessage`, INS 0x08): the first chunk is the path,
 * then the message's byte length as four big-endian bytes, then the first
 * bytes; every later chunk is bytes only. Chunks are 150 bytes. Until 22 Oct
 * 2026 (Astra, max) this used the TRANSACTION framing — no length, the
 * transaction's chunk size and P1 flags — and the device would have refused
 * the registration proof.
 */
export function messageChunks(path: number[], bytes: Uint8Array): Uint8Array[] {
  const head = encodePath(path);
  if (bytes.length === 0) throw new LedgerError('Nothing to sign', 'The message is empty. Report this.');
  const length = new Uint8Array(4);
  new DataView(length.buffer).setUint32(0, bytes.length, false);
  const chunks: Uint8Array[] = [];
  let at = 0;
  let first = true;
  while (at < bytes.length) {
    const room = MESSAGE_CHUNK - (first ? head.length + length.length : 0);
    const piece = bytes.subarray(at, at + room);
    chunks.push(first ? new Uint8Array([...head, ...length, ...piece]) : piece);
    at += piece.length;
    first = false;
  }
  return chunks;
}

/** P1 for each SIGN PERSONAL MESSAGE command: 0x00 for the first chunk, 0x80 for every later one — the app signs when the declared length is reached. */
export function messageFlags(count: number): number[] {
  return Array.from({ length: count }, (_, i) => (i === 0 ? P1_SIGN_FIRST : P1_SIGN_MORE));
}

export interface LedgerMessageSigning {
  hid: HidLike;
  network: Network;
  /** The key that signs: a full path on the device (accountPath(0) is the registered device's own address). */
  path: number[];
  /** The text the device shows and signs. */
  message: string;
  onStep: (step: LedgerStep, text: string) => void;
}

/**
 * Have the device sign a personal message with the key at `path`. The device
 * shows the text; the operator reads it there and approves. Used to vouch
 * for a float's account key at registration (signer/src/key-approval.mjs,
 * audit of 21 Oct 2026, D2) — the registered device signs what the desk is
 * about to register, and the Worker recovers the signer.
 *
 * Returns r ‖ s ‖ v as the app writes it (v = 0 or 1), 65 bytes of hex.
 */
export async function signMessageOnLedger(o: LedgerMessageSigning): Promise<{ signature: string; path: string }> {
  if (!Array.isArray(o.path) || o.path.length < 2) throw new LedgerError('No key named for the message', 'Report this.');
  const bytes = new TextEncoder().encode(o.message);
  const chunks = messageChunks(o.path, bytes);
  const flags = messageFlags(chunks.length);
  const { link, release } = await openTronApp(o.hid, o.network, o.onStep, 'Sign');
  try {
    o.onStep('key', `Sending the registration message to the key at ${pathText(o.path)} on your Ledger…`);
    let answer: Uint8Array = new Uint8Array(0);
    for (let i = 0; i < chunks.length; i++) {
      const last = i === chunks.length - 1;
      answer = await ask(
        link, apdu(CLA_TRON, INS_SIGN_MESSAGE, flags[i]!, 0, chunks[i]!), o.network, true,
        last ? SIGN_WAIT_MS : ANSWER_WAIT_MS,
        // The TRON app shows a personal message as its HASH, never as text
        // (0.7.x): there is nothing to read back but the fact of the request.
        last ? () => o.onStep('sign', 'Now approve the message on your Ledger — it shows the message’s hash, not the text.') : undefined,
      );
    }
    if (answer.length < 65) {
      throw new LedgerError('The Ledger did not return a signature', 'Nothing was registered. Report this.');
    }
    return { signature: hexOf(answer.subarray(0, 65)), path: pathText(o.path) };
  } finally {
    release();
    await link.close();
  }
}

// ---- registering an account key ---------------------------------------------

export interface LedgerExport {
  hid: HidLike;
  /** The TRON account on the device, as Ledger Live numbers them from 0: account 0 is m/44'/195'/0'. */
  account: number;
  network: Network;
  /** Each step, for the status line. 'show' is the moment the first address is on the device's screen. */
  onStep: (step: LedgerStep, text: string) => void;
}

export interface AccountKeyExport {
  /** The account's public key, 65 bytes of hex (04, X, Y), as the app answers it. */
  publicKey: string;
  /** The account's chain code, 32 bytes of hex. With the key above, the account's xpub. */
  chainCode: string;
  /** The fingerprint of m/44'/195', eight hex characters — the xpub names its parent by it. */
  parentFingerprint: string;
  /** The account's path, m/44'/195'/<account>'. */
  path: string;
  account: number;
  /** The address at …/<account>'/0/0, as the device showed it and the person confirmed it. */
  firstAddress: string;
}

/**
 * Export an account's PUBLIC key and chain code over the cable — what the desk
 * expands into one deposit address per deal — and have the device show the
 * account's first address for the person to check.
 *
 * Nothing here can spend: a public key and a chain code lay bare every
 * address the account will ever use, and nothing else. The desk derives the
 * first address from the two itself and refuses the key when it is not the
 * one the device showed (bff/src/routes/custody-keys.ts).
 */
export async function exportAccountKey(o: LedgerExport): Promise<AccountKeyExport> {
  if (!Number.isInteger(o.account) || o.account < 0 || o.account >= HARDENED) {
    throw new LedgerError('The account number is not one a Ledger has', 'Report this.');
  }
  const { link, release } = await openTronApp(o.hid, o.network, o.onStep, 'Register');
  try {
    // The account's key with its chain code, and the parent's key for the
    // fingerprint. Nothing is shown on the device for either.
    o.onStep('key', `Reading the key of account ${o.account + 1} from your Ledger…`);
    const key = parseAccountKeyAnswer(await ask(link, apdu(CLA_TRON, INS_ADDRESS, 0, P2_CHAIN_CODE, encodePath(accountKeyPath(o.account))), o.network));
    const parent = parseAddressAnswer(await ask(link, apdu(CLA_TRON, INS_ADDRESS, 0, 0, encodePath(COIN_PATH)), o.network));

    // The first address, ON the device: the person compares it with the one
    // the page shows and presses. The app answers once they have.
    let shown: Uint8Array;
    try {
      shown = await ask(
        link, apdu(CLA_TRON, INS_ADDRESS, P1_ADDRESS_SHOW, 0, encodePath([...accountKeyPath(o.account), 0, 0])), o.network, false,
        SIGN_WAIT_MS,
        () => o.onStep('show', 'Now check the first address of this account on your Ledger’s screen against this page, and confirm it there.'),
      );
    } catch (err) {
      if (err instanceof LedgerError && err.status === 0x6985) {
        throw new LedgerError('Rejected on the device', 'Nothing was registered. Tap Register again when you are ready, and confirm the address on the Ledger.', err.status);
      }
      throw err;
    }
    const { address: firstAddress } = parseAddressAnswer(shown);
    return {
      publicKey: hexOf(key.publicKey),
      chainCode: hexOf(key.chainCode),
      parentFingerprint: keyFingerprint(parent.publicKey),
      path: pathText(accountKeyPath(o.account)),
      account: o.account,
      firstAddress,
    };
  } finally {
    release();
    await link.close();
  }
}
